import React, { useEffect, useMemo, useState } from 'react';

import { List, Modal } from 'antd';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import styled from 'styled-components';

import { useUserPermission } from '@contexts/UserContext';

import { getBookCover } from '@libs/NwUtils';

import { forceReloadContact } from '@hooks/API/Contact/UseContact';
import { forceReloadContactPlaylists } from '@hooks/API/Contact/UseContactPlaylists';
import { forceReloadPlaylist, forceReloadPlaylistInfo } from '@hooks/API/UsePlaylist';
import { useAddPlaylist, useRemovePlayList, getWebsitePlaylists } from '@hooks/API/UseWebsites';

import CreatedBy from '@components/CreatedBy/CreatedBy';
import AvatarBg from '@components/Gui/AvatarBg';
import { Loading } from '@components/Gui/Loading';
import { NwButton, NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwDrawer from '@components/Gui/NwDrawer';
import { NWSelect } from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';
import ModelDetailsBooksPublicationsDrawer from '@components/ContactDetails/ModelDetails/CommonComponents/ModelDetailsBooks/ModelDetailsBooksPublicationsDrawer';
import { OtherBooksPublishedContainer } from '@components/MediaDrawer/PlaylistPublishedWebsitesUI';
import PlaylistPublishedWebsitesList from '@components/MediaDrawer/PlaylistPublishedWebsitesList';
import { usePlaylistPublishedWebsites } from '@components/MediaDrawer/usePlaylistPublishedWebsites';

import { coercePlaylistArray } from '../ModelDetailsBooks/modelDetailsBooks.utils';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const { confirm } = Modal;

const PublishDrawerColumns = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1.5rem;
    align-items: start;

    @media only screen and (max-width: 1199px) {
        grid-template-columns: 1fr;
    }
`;

const PublishDrawerColumn = styled.div`
    min-width: 0;
`;

const PublishDrawerRightColumn = styled(PublishDrawerColumn)`
    border-left: 1px solid ${(props) => props.theme.BorderColor};
    padding-left: 1.5rem;

    @media only screen and (max-width: 1199px) {
        border-left: none;
        border-top: 1px solid ${(props) => props.theme.BorderColor};
        padding-left: 0;
        padding-top: 1.5rem;
    }
`;

const ModelBookPublishDrawer = ({ onClose, websiteData, books, selectedBook }) => {
    const canPublishBooks = useUserPermission('PublishBooks');
    const [touchForm, setTouchForm] = useState(false);
    const [book, setBook] = useState(selectedBook);
    const [otherBooks, setOtherBooks] = useState([]);
    const [currentSectionCode, setCurrentSectionCode] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [removingPublicationKey, setRemovingPublicationKey] = useState(null);
    const { mutateAsync: onAddPlaylist } = useAddPlaylist();
    const { mutateAsync: onRemovePlayList } = useRemovePlayList();
    const resolvedBooks = useMemo(() => coercePlaylistArray(books) || [], [books]);

    const sortedWebsiteData = useMemo(() => {
        return [...websiteData].sort((a, b) => a.Code.localeCompare(b.Code));
    }, [websiteData]);
    const [currentWebsite, setCurrentWebsite] = useState(sortedWebsiteData[0]);

    const websiteOptions = sortedWebsiteData.map((website, index) => {
        return { key: website.Code, label: website.Name, value: index };
    });

    const _publishAsOptions = [
        {
            key: 'Portfolio',
            label: 'Main Portfolio',
            value: 'Portfolio',
            disabled: false,
        },
        {
            key: 'Digitals',
            label: 'Digitals',
            value: 'Digitals',
            disabled: false,
        },
        {
            key: 'CompCard',
            label: 'Comp Card',
            value: 'CompCard',
            disabled: false,
        },
        { key: 'Other', label: 'Other', value: 'Other', disabled: false },
    ];

    const _initialValues = {
        websiteCode: websiteOptions[0]?.value ?? 0,
        publishAs: '',
        section: '',
    };

    const [initialValues, setInitialValues] = useState(_initialValues);
    const [publishAsOptions, setPublishAsOptions] = useState(_publishAsOptions);

    const isOptionDisabled = (option, otherBooksInSameDivision) => {
        if (option.value === 'Portfolio') {
            if (otherBooksInSameDivision.find((book) => book.Role === 'Portfolio')) {
                return true;
            }
        }
        return false;
    };

    const makeSectionOptions = (data, path, linkedSections) => {
        const options = [];
        for (const item of data) {
            if (item.type === 'list') {
                const _path = `${path.slice(2)} > ${item.name.toUpperCase()}`;
                if (
                    !linkedSections.find(
                        (item) =>
                            item.path.trim().toLowerCase() === _path.trim().toLowerCase() &&
                            item.websiteCode.trim().toLowerCase() === currentWebsite.Code.trim().toLowerCase()
                    )
                ) {
                    options.push({
                        key: item.code,
                        value: item.code,
                        label: _path,
                    });
                }
            } else if (item.subNav && item.subNav.length) {
                options.push(
                    ...makeSectionOptions(item.subNav, `${path} > ${item.name.toUpperCase()}`, linkedSections)
                );
            }
        }
        return options;
    };

    async function updateOtherBooks() {
        if (!book || !currentWebsite || !currentSectionCode) {
            setOtherBooks([]);
            setPublishAsOptions(_publishAsOptions);
            return;
        }
        try {
            const allbooks = await getWebsitePlaylists(currentWebsite.Code);
            const otherBooksInSameDivision = allbooks.filter(
                (item) =>
                    item.DivisionCode.toLowerCase() === currentSectionCode.toLowerCase() &&
                    book.ContactID === item.Playlist.ContactID
            );
            setOtherBooks(otherBooksInSameDivision);
            setPublishAsOptions(
                _publishAsOptions.map((option) => {
                    return {
                        ...option,
                        disabled: isOptionDisabled(option, otherBooksInSameDivision),
                    };
                })
            );
        } catch (error) {
            console.error('error in updateOtherBooks', error);
        }
    }

    const { linkedSections, loading: unlinkingPublication, unlinkPublication } = usePlaylistPublishedWebsites({
        book,
        websiteData: sortedWebsiteData,
        onAfterUnlink: () => {
            updateOtherBooks();
        },
    });

    const sectionOptions = useMemo(() => {
        if (!currentWebsite?.Structure?.nav) {
            return [];
        }

        return makeSectionOptions(currentWebsite.Structure.nav, '', linkedSections);
    }, [currentWebsite, linkedSections]);

    useEffect(() => {
        const newBook = resolvedBooks.find((item) => item.ID === book?.ID);
        if (newBook) {
            setBook(newBook);
        }
    }, [book?.ID, resolvedBooks]);

    useEffect(() => {
        updateOtherBooks();
    }, [book, currentSectionCode, currentWebsite]);

    useEffect(() => {
        if (!currentWebsite && sortedWebsiteData.length > 0) {
            setCurrentWebsite(sortedWebsiteData[0]);
        }
    }, [currentWebsite, sortedWebsiteData]);

    const onPublish = async (values, setSubmitting) => {
        if (!canPublishBooks) {
            setSubmitting(false);
            return;
        }

        const data = { ...values };
        const selectedWebsite = sortedWebsiteData[data.websiteCode];
        setSubmitting(true);
        try {
            await onAddPlaylist({
                data: {
                    WebsiteCode: selectedWebsite?.Code || currentWebsite?.Code,
                    DivisionCode: data.section,
                    Role: data.publishAs,
                    PlaylistID: book.ID,
                },
            });
            forceReloadContact(book.ContactID);
            forceReloadPlaylist(book.ID);
            forceReloadPlaylistInfo(book.ID);
            forceReloadContactPlaylists(book.ContactID);
            onClose();
        } catch (error) {
            console.error('error publishing playlist on website', error);
        } finally {
            setSubmitting(false);
        }
    };

    const unlinkOtherPlaylist = async (item) => {
        if (!canPublishBooks) {
            return;
        }

        setRefreshing(true);
        try {
            await onRemovePlayList({
                WebsiteCode: item.websiteCode,
                PlaylistID: item.playlistID,
                DivisionCode: item.divisionCode,
            });
            if (book?.ContactID) {
                forceReloadContact(book.ContactID);
                forceReloadContactPlaylists(book.ContactID);
            }
            updateOtherBooks();
        } catch (error) {
            console.error('error unlinking other playlist publication', error);
        } finally {
            setRefreshing(false);
        }
    };

    const confirmUnlinkOtherPlaylist = (item) => {
        if (!canPublishBooks) {
            return;
        }

        confirm({
            title: <div>Remove website publication?</div>,
            onOk() {
                unlinkOtherPlaylist(item);
            },
            onCancel() {},
        });
    };

    const handleRemoveModelPublication = async (item) => {
        setRemovingPublicationKey(item.key);

        try {
            await onRemovePlayList({
                WebsiteCode: item.WebsiteCode,
                PlaylistID: item.Playlist?.ID,
                DivisionCode: item.DivisionCode,
            });

            if (item.Playlist?.ContactID) {
                forceReloadContact(item.Playlist.ContactID);
                forceReloadContactPlaylists(item.Playlist.ContactID);
            }

            if (item.Playlist?.ID) {
                forceReloadPlaylist(item.Playlist.ID);
                forceReloadPlaylistInfo(item.Playlist.ID);
            }

            if (Number(item.Playlist?.ID) === Number(book?.ID)) {
                setBook((currentBook) => {
                    if (!currentBook?.Divisions?.length) {
                        return currentBook;
                    }

                    return {
                        ...currentBook,
                        Divisions: currentBook.Divisions.filter(
                            (division) =>
                                !(
                                    division?.WebsiteCode === item.WebsiteCode &&
                                    division?.DivisionCode === item.DivisionCode
                                )
                        ),
                    };
                });
            }

            updateOtherBooks();
            return true;
        } catch (error) {
            console.error('error removing model publication from publish drawer', error);
            return false;
        } finally {
            setRemovingPublicationKey(null);
        }
    };

    return (
        <NwDrawer
            touchForm={touchForm}
            onTouchForm={setTouchForm}
            onClose={onClose}
            title="Publish book on website"
            width="80rem"
        >
            <PublishDrawerColumns>
                <PublishDrawerColumn>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={(values, { setSubmitting }) => {
                            onPublish(values, setSubmitting);
                        }}
                        validationSchema={Yup.object().shape({
                            section: Yup.string().required('This field is required'),
                            publishAs: Yup.string().required('This field is required'),
                        })}
                    >
                        {({ isSubmitting, values, handleSubmit, setFieldValue }) => (
                            <NwForm values={values} onTouchForm={setTouchForm} onFinish={handleSubmit}>
                                <NwDivider textSize="medium">THIS BOOK IS PUBLISHED ON</NwDivider>
                                <PlaylistPublishedWebsitesList
                                    linkedSections={linkedSections}
                                    loading={unlinkingPublication}
                                    onUnlink={canPublishBooks ? unlinkPublication : null}
                                    showActions={canPublishBooks}
                                />
                                <NwDivider textSize="medium">PUBLISH</NwDivider>
                                <div>
                                    <Field
                                        component={NWSelect}
                                        label="On website"
                                        name="websiteCode"
                                        placeholder=""
                                        value={values.websiteCode}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        options={websiteOptions}
                                        onAfterChange={(value) => {
                                            setCurrentWebsite(sortedWebsiteData[value]);
                                            setFieldValue('section', '');
                                            setCurrentSectionCode('');
                                            setInitialValues({
                                                ...initialValues,
                                                websiteCode: value,
                                                section: '',
                                            });
                                        }}
                                    />
                                    <Field
                                        component={NWSelect}
                                        label="In section"
                                        name="section"
                                        placeholder="Select webpath"
                                        value={values.section}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        options={sectionOptions}
                                        onAfterChange={(value) => {
                                            setCurrentSectionCode(value);
                                            setFieldValue('publishAs', '');
                                            setInitialValues({
                                                ...initialValues,
                                                section: value,
                                                publishAs: '',
                                            });
                                        }}
                                    />
                                    <Field
                                        component={NWSelect}
                                        label="as"
                                        name="publishAs"
                                        placeholder=""
                                        value={values.publishAs}
                                        getPopupContainer={(triggerNode) => triggerNode.parentNode}
                                        options={publishAsOptions}
                                        onAfterChange={(value) => {
                                            setInitialValues({
                                                ...initialValues,
                                                publishAs: value,
                                            });
                                        }}
                                    />
                                </div>
                                <br />
                                <NwFormButtonsBar
                                    left={
                                        <NwCancelButton
                                            disabled={isSubmitting}
                                            onClick={() => {
                                                setTouchForm(false);
                                                onClose();
                                            }}
                                        />
                                    }
                                    right={
                                        <NwSaveButton
                                            htmlType="submit"
                                            disabled={isSubmitting || !canPublishBooks}
                                            loading={isSubmitting}
                                        />
                                    }
                                />
                                <br />
                                <NwDivider textSize="medium">OTHER MODEL'S BOOKS IN THIS SECTION</NwDivider>
                                {refreshing ? (
                                    <Loading textBlack />
                                ) : (
                                    <List
                                        itemLayout="horizontal"
                                        dataSource={otherBooks}
                                        renderItem={(book) => (
                                            <List.Item
                                                actions={
                                                    canPublishBooks
                                                        ? [
                                                              <NwButton
                                                                  iconOnly
                                                                  ghost
                                                                  disabled={refreshing}
                                                                  loading={refreshing}
                                                                  icon={byPrefixAndName.fasl['trash-alt']}
                                                                  onClick={() =>
                                                                      confirmUnlinkOtherPlaylist({
                                                                          divisionCode: book.DivisionCode,
                                                                          websiteCode: book.WebsiteCode,
                                                                          playlistID: book.Playlist.ID,
                                                                      })
                                                                  }
                                                              />,
                                                          ]
                                                        : []
                                                }
                                            >
                                                <List.Item.Meta
                                                    avatar={<AvatarBg src={getBookCover(book.Playlist)} />}
                                                    title={`${book.Playlist.Name.toUpperCase()}`}
                                                    description={
                                                        <OtherBooksPublishedContainer>
                                                            <CreatedBy object={book.Playlist} short textonly />
                                                            <p>
                                                                PUBLISHED AS <strong>{book.Role}</strong>
                                                            </p>
                                                        </OtherBooksPublishedContainer>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                )}
                            </NwForm>
                        )}
                    </Formik>
                </PublishDrawerColumn>
                <PublishDrawerRightColumn>
                    <ModelDetailsBooksPublicationsDrawer
                        contactID={book?.ContactID}
                        onRemovePublication={handleRemoveModelPublication}
                        readOnly={!canPublishBooks}
                        removingPublicationKey={removingPublicationKey}
                        renderAsDrawer={false}
                        websiteData={sortedWebsiteData}
                    />
                </PublishDrawerRightColumn>
            </PublishDrawerColumns>
        </NwDrawer>
    );
};

export default ModelBookPublishDrawer;
